import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Badge, Collapse } from 'reactstrap';
import {
    updateUi,
    getLastHealthCheck,
    checkHealth,
    loadTickettypes,
    loadLayouts,
    loadEmailLayouts,
    loadActivities,
    loadBucketlists,
} from '../../redux';
import { Health, Utils } from '../../services';
import { pathForHealthCheckLink, isExternalHealthCheckLink, getHealthCheckLinkParts } from '../../services/healthCheckLinks';
import {
    inferHealthCheckLinksFromDetails,
    inferHealthCheckLinksFromTest,
    resolve_health_check_links,
    formatHealthCheckDetails,
    enrich_health_check_links_with_issues,
    should_hide_raw_health_check_details,
} from '../../services/healthCheckInferLinks';

import './HealthChecks.css';

export const LAST_HEALTH_CHECK_POLL_MS = 60 * 1000;

const HealthCheckIssueList = ({ issues }) => (
    issues.map((issue, index) => (
        <React.Fragment key={`${issue}-${index}`}>
            {index > 0 ? ', ' : null}
            <i>{issue}</i>
        </React.Fragment>
    ))
);

const HealthCheckLinkRow = ({ link, t, context, onNavigate }) => {
    const { openLabel, fixSuffix, issues } = getHealthCheckLinkParts(link, t, context);
    const fixPart = fixSuffix ? (
        <span className="health-check-link-fix">
            {fixSuffix}
            <HealthCheckIssueList issues={issues} />
        </span>
    ) : null;

    if (isExternalHealthCheckLink(link)) {
        return (
            <div className="health-check-link-row">
                <a
                    href={link.id}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="health-check-deep-link"
                    title={openLabel}
                    onClick={onNavigate}
                >
                    <i className="fa fa-external-link" aria-hidden="true" />
                    &nbsp;{openLabel}
                </a>
                {fixPart}
            </div>
        );
    }

    const to = pathForHealthCheckLink(link);
    if (!to)
        return null;
    return (
        <div className="health-check-link-row">
            <Link to={to} className="health-check-deep-link" title={openLabel} onClick={onNavigate}>
                <i className="fa fa-external-link" aria-hidden="true" />
                &nbsp;{openLabel}
            </Link>
            {fixPart}
        </div>
    );
};

const HealthChecks = () => {
    const dispatch             = useDispatch();
    const { t, i18n }          = useTranslation();
    const ui_lang              = i18n.resolvedLanguage || i18n.language;
    const { showHealthChecks, isMobile } = useSelector(state => state.ui);
    const { log, isLoading }   = useSelector(state => state.health);
    const { tickettypes }      = useSelector(state => state.tickettypes);
    const { activities }       = useSelector(state => state.activities);
    const pdfLayouts           = useSelector(state => state.layouts.layouts);
    const emailLayouts         = useSelector(state => state.emailLayouts.layouts);
    const bucketlists          = useSelector(state => state.bucketlists.bucketlists);
    const [ignoredOpen, setIgnoredOpen] = useState(false);
    const isLoadingRef = useRef(isLoading);
    isLoadingRef.current = isLoading;

    useEffect(() => {
        dispatch(getLastHealthCheck());
        const intervalId = setInterval(() => {
            if (!isLoadingRef.current)
                dispatch(getLastHealthCheck());
        }, LAST_HEALTH_CHECK_POLL_MS);
        return () => clearInterval(intervalId);
    }, [dispatch]);

    // Only needed to make check details readable, so load them lazily with the panel.
    useEffect(() => {
        if (!showHealthChecks)
            return;
        dispatch(loadTickettypes());
        dispatch(loadLayouts());
        dispatch(loadEmailLayouts());
        dispatch(loadActivities());
        dispatch(loadBucketlists());
    }, [dispatch, showHealthChecks]);

    const healthContext = useMemo(() => ({
        tickettypes: tickettypes || [],
        activities: activities || [],
        layouts: [...(pdfLayouts || []), ...(emailLayouts || [])],
        bucketlists: bucketlists || [],
        lang: ui_lang,
    }), [tickettypes, activities, pdfLayouts, emailLayouts, bucketlists, ui_lang]);

    const runHealthChecks = () => {
        dispatch(checkHealth());
    };

    const hideHealthChecks = () => {
        dispatch(updateUi({ showHealthChecks: !showHealthChecks }));
    };

    const nbPassed = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(tst => !tst.ignored && !!tst.result).length);
        }, 0);
    }, [log]);
    const nbIgnored = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(tst => !!tst.ignored).length);
        }, 0);
    }, [log]);
    const nbFailed = useMemo(() => {
        return Object.keys(log?.results || {}).reduce((nb, group) => {
            return (nb += log.results[group].filter(tst => !tst.ignored && !tst.result).length);
        }, 0);
    }, [log]);

    const formatted_health_date = Utils.formatLocalizedDateTime(log?.created_at, ui_lang, 'LLL');
    const panel_ref = useRef(null);

    useEffect(() => {
        if (!showHealthChecks)
            return undefined;
        const timer = window.setTimeout(() => {
            const first_failed = panel_ref.current?.querySelector('.test.bg-danger');
            first_failed?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 250);
        return () => window.clearTimeout(timer);
    }, [showHealthChecks, log]);

    const logResume = useMemo(() => {
        const keys = Object.keys(log?.results || {});
        return (
        <div>
            <h4 className="text-center text-underline mb-2">
                { t('health.run_at', { date: formatted_health_date }) }
            </h4>
            <div className="health-check-summary-badges d-flex flex-wrap justify-content-center align-items-center mb-3">
                <Badge color="success" className="m-1">{t('health.summary.passed', { count: nbPassed })}</Badge>
                <Badge color="danger" className="m-1">{t('health.summary.failed', { count: nbFailed })}</Badge>
                <Badge color="secondary" className="m-1">{t('health.summary.ignored', { count: nbIgnored })}</Badge>
            </div>

            {keys.map(key => {
                const active = (log.results[key] || []).filter(c => !c.ignored);
                if (!active.length)
                    return null;
                return (
                    <div key={key} className="dark mt-3">
                        <div className="text-bold mb-1">
                            <i className={`fa fa-${Health.groupIcon(key)}`} />&nbsp;
                            {t(`health.groups.${key}.title`)}
                        </div>
                        {active.map((checkItem, index) => {
                            const failed = !checkItem.result;
                            const hintKey = `health.groups.${key}.${checkItem.test}_hint`;
                            const showHint = failed && i18n.exists(hintKey);
                            const sitePrefixMatch = typeof checkItem.details === 'string'
                                ? checkItem.details.match(/^(.+?)\s*->\s*/)
                                : null;
                            const siteName = failed
                                && typeof checkItem.test === 'string'
                                && checkItem.test.startsWith('integrations.wordpress.')
                                && sitePrefixMatch
                                ? sitePrefixMatch[1].trim()
                                : '';
                            const failedTitleKey = `health.groups.${key}.${checkItem.test}_failed`;
                            const siteTitleKey = `${failedTitleKey}_site`;
                            const checkTitle = failed && siteName && i18n.exists(siteTitleKey)
                                ? t(siteTitleKey, { site: siteName })
                                : t(`health.groups.${key}.${checkItem.test}${checkItem.result ? '' : '_failed'}`);
                            const inferred = failed
                                ? [
                                    ...inferHealthCheckLinksFromDetails(key, checkItem.test, checkItem.details),
                                    ...inferHealthCheckLinksFromTest(key, checkItem.test),
                                ]
                                : [];
                            const mergedLinks = failed
                                ? enrich_health_check_links_with_issues(
                                    checkItem.test,
                                    resolve_health_check_links(checkItem.test, [
                                        ...(checkItem.links || []),
                                        ...inferred,
                                    ]),
                                    checkItem.details,
                                )
                                : [];
                            const linkKeys = new Set();
                            const uniqueLinks = mergedLinks.filter((lnk) => {
                                const k = [
                                    lnk.resource,
                                    lnk.id,
                                    lnk.tab || '',
                                    lnk.widget || '',
                                    lnk.lang || '',
                                ].join(':');
                                if (linkKeys.has(k))
                                    return false;
                                linkKeys.add(k);
                                return true;
                            });
                            const hideRawDetails = failed
                                && should_hide_raw_health_check_details(checkItem.test, uniqueLinks);
                            const detailText = failed && checkItem.details && !hideRawDetails
                                ? formatHealthCheckDetails(t, key, checkItem.test, checkItem.details, healthContext)
                                : null;
                            return (
                                <div className={`test bg-${checkItem.result ? 'success' : 'danger'}`} key={`${key}-${index}`}>
                                    <div className="name">
                                        {checkTitle}
                                        {showHint && (
                                            <small className="health-check-hint">{t(hintKey)}</small>
                                        )}
                                        {failed && detailText && (
                                            <small className="details">{detailText}</small>
                                        )}
                                        {failed && uniqueLinks.map((lnk, li) => (
                                            <HealthCheckLinkRow
                                                key={`${lnk.resource}-${lnk.id}-${lnk.tab || ''}-${lnk.widget || ''}-${lnk.lang || ''}-${li}`}
                                                link={lnk}
                                                t={t}
                                                context={healthContext}
                                                onNavigate={isMobile
                                                    ? () => dispatch(updateUi({ showHealthChecks: false }))
                                                    : undefined}
                                            />
                                        ))}
                                    </div>
                                    <div className="result">
                                        <Badge color={checkItem.result ? 'success' : 'danger'} size="xs">
                                            <i className={`fa fa-${checkItem.result ? 'check' : 'warning'}`} />
                                        </Badge>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
        );
    }, [log, formatted_health_date, nbPassed, nbFailed, nbIgnored, t, i18n, healthContext, isMobile, dispatch]);

    const ignoredBlocks = Object.keys(log?.results || {}).map(key => {
        const ignored = (log?.results?.[key] || []).filter(c => c.ignored);
        if (!ignored.length)
            return null;
        return (
            <div key={`ig-${key}`} className="dark mt-2">
                <div className="text-bold mb-1 small">
                    {t(`health.groups.${key}.title`)}
                </div>
                {ignored.map((checkItem, index) => (
                    <div className="test bg-black" key={`${key}-ig-${index}`}>
                        <div className="name">
                            {t(`health.groups.${key}.${checkItem.test}`)}
                            {checkItem.details && (
                                <small className="details">{checkItem.details}</small>
                            )}
                        </div>
                        <div className="result">
                            <Badge color="dark" size="xs">
                                <i className="fa fa-circle-o" />
                            </Badge>
                        </div>
                    </div>
                ))}
            </div>
        );
    }).filter(Boolean);

    return (
        <div
            ref={panel_ref}
            className={`HealthChecks${showHealthChecks ? " expanded d-block" : " reduced"}`}
        >
            {(showHealthChecks) &&
                <>
                    <Button color="danger" size="sm" className='float-right' onClick={hideHealthChecks}>
                        <i className='fa fa-times'></i>
                    </Button>
                    <h3 className='border-bottom mt-1'>
                        <i className='fa fa-medkit'></i>&nbsp;{t('health.panel_title')}
                    </h3>
                    <small>
                        { t('health.check_desc') }
                    </small>
                    <hr />
                    <div className="text-center">
                        <Button onClick={ runHealthChecks } disabled={isLoading} size="block" color="info" title={t("health.check")} className="mt-2">
                            <i className='fa fa-bolt'></i>&nbsp;
                            {t('health.check')}
                        </Button>
                    </div>
                    <hr />
                    {log && logResume}
                    {log && nbIgnored > 0 && (
                        <>
                            <button
                                type="button"
                                className="health-check-ignored-toggle btn btn-link text-white p-0 text-left w-100"
                                onClick={() => setIgnoredOpen(o => !o)}
                            >
                                <i className={`fa fa-chevron-${ignoredOpen ? 'down' : 'right'} mr-1`} aria-hidden="true" />
                                <span className="text-bold">{t('health.groups.ignored')}</span>
                                <Badge color="secondary" className="ml-2">{nbIgnored}</Badge>
                            </button>
                            <Collapse isOpen={ignoredOpen}>
                                {ignoredBlocks}
                            </Collapse>
                        </>
                    )}
                    {isLoading && (
                        <div className="text-center">
                            <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                        </div>
                    )}
                </>
            }
        </div>
    );
}

export default HealthChecks;
